/**
 * Mixin para facilitar o uso do utilitário seloImageUtils em componentes Vue
 */

import seloImageManager, { SeloImageManager } from "@/helpers/seloImageUtils";

export default {
  data() {
    return {
      // Constantes de medalhas disponíveis
      MEDAL_TYPES: SeloImageManager.MEDAL_NAMES,
    };
  },

  methods: {
    /**
     * Obtém a URL de uma medalha com fallback por ano
     * @param {string} imageName - Nome da imagem da medalha
     * @returns {string} URL da imagem
     */
    getSeloImageUrl(imageName) {
      return seloImageManager.getImageUrl(imageName);
    },

    /**
     * Manipula erros de carregamento de imagem
     * @param {Event} event - Evento de erro da imagem
     * @param {string} imageName - Nome da imagem
     * @returns {boolean} true se conseguiu fallback, false se esgotaram as opções
     */
    handleSeloImageError(event, imageName) {
      return seloImageManager.handleImageError(event, imageName);
    },

    /**
     * Pré-carrega uma imagem e retorna a URL válida
     * @param {string} imageName - Nome da imagem
     * @returns {Promise<string>} URL da imagem que funciona
     */
    async preloadSeloImage(imageName) {
      try {
        return await seloImageManager.preloadImage(imageName);
      } catch (error) {
        console.error(`Erro ao pré-carregar imagem ${imageName}:`, error);
        return null;
      }
    },

    /**
     * Pré-carrega todas as medalhas
     * @returns {Promise<Object>} Objeto com as URLs válidas de todas as medalhas
     */
    async preloadAllSeloImages() {
      const results = {};
      const medals = Object.values(SeloImageManager.MEDAL_NAMES);

      for (const medal of medals) {
        try {
          results[medal] = await this.preloadSeloImage(medal);
        } catch (error) {
          console.error(`Erro ao carregar medalha ${medal}:`, error);
          results[medal] = null;
        }
      }

      return results;
    },

    /**
     * Limpa o cache de uma imagem específica
     * @param {string} imageName - Nome da imagem
     */
    clearSeloImageCache(imageName) {
      seloImageManager.clearImageCache(imageName);
    },

    /**
     * Limpa todo o cache de imagens dos selos
     */
    clearAllSeloImageCache() {
      seloImageManager.clearAllCache();
    },

    /**
     * Obtém todas as medalhas com suas informações
     * @returns {Array} Array com objetos contendo name e label das medalhas
     */
    getAllMedals() {
      return [
        { name: this.MEDAL_TYPES.BRONZE, label: "Bronze" },
        { name: this.MEDAL_TYPES.PRATA, label: "Prata" },
        { name: this.MEDAL_TYPES.OURO, label: "Ouro" },
        { name: this.MEDAL_TYPES.DIAMANTE, label: "Diamante" },
      ];
    },
  },

  /**
   * Exemplo de uso do mixin:
   *
   * <template>
   *   <div>
   *     <img
   *       :src="getSeloImageUrl(MEDAL_TYPES.BRONZE)"
   *       @error="handleSeloImageError($event, MEDAL_TYPES.BRONZE)"
   *       alt="Medalha Bronze"
   *     />
   *   </div>
   * </template>
   *
   * <script>
   * import seloImageMixin from '@/mixins/seloImageMixin';
   *
   * export default {
   *   mixins: [seloImageMixin],
   *
   *   async mounted() {
   *     // Pré-carrega todas as medalhas
   *     const preloadedImages = await this.preloadAllSeloImages();
   *     console.log('Imagens pré-carregadas:', preloadedImages);
   *   }
   * };
   * </script>
   */
};
